import React, { useEffect, useState, useRef, useCallback } from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'
import { selectUnit } from '@formatjs/intl-utils'
import Icon from 'components/Icon'
import { statuses } from '../../constants'
import useCurrentUser from 'hooks/useCurrentUser'
import { useHistory } from 'react-router-dom'
import { Chip, Button } from '@arnsbogroup/react-voicearchive'
import NiceModal from '@ebay/nice-modal-react'
import ImageModal from './components/ImageModal'

const DemoPlayer = ({ file, type }) => {
  const [playing, setPlaying] = useState(false)
  const audioRef = useRef()

  useEffect(() => {
    if (audioRef.current.paused && playing) {
      audioRef.current.play()
    } else {
      audioRef.current.pause()
    }
  }, [playing])

  const onClick = (event) => {
    event.stopPropagation()
    setPlaying(!playing)
  }

  return (
    <div className='flex gap-1'>
      <span className="truncate" onClick={onClick}>
        <span className={file?.cdn_url ? 'text-primary cursor-pointer' : 'text-gray-400 line-through cursor-not-allowed'}>
          { playing ? 'Pause' : type }
        </span>

        <audio controls src={file?.cdn_url} className="hidden" ref={audioRef} onEnded={() => setPlaying(false)} preload="none">
          HTML5 <code>audio</code> not supported.
        </audio>
      </span>
      <Icon className={file?.cdn_url ? 'text-xs my-auto text-primary cursor-pointer' : 'text-xs my-auto text-gray-400 cursor-not-allowed'} icon={['fa', 'download']} onClick={() => file?.id ? window.open(`/download/supplier/demo/${file?.id}`, '_blank') : undefined} />
    </div>
  )
}

const Supplier = ({ supplier, expanded, onExpand: onExpandCallback, onCollapse, onChange, onReset, onSuspiciousChange }) => {
  const { roles, id, features = [] } = useCurrentUser()
  const beta = features.includes('next-coordinator-ui')
  const history = useHistory()

  const onStudioSetupPreview = (src) => {
    NiceModal.show(ImageModal, { src: src })
  }

  const onExpand = () => {
    if (expanded) {
      onCollapse()
    } else {
      onExpandCallback()
    }
  }

  return (
    <div className={`border-b border-gray-400 ${expanded ? 'bg-gray-300' : 'hover:bg-gray-100'}`}>
      <div className="flex">
        <div className="w-2/12 px-4 py-2 truncate text-primary cursor-pointer" onClick={onExpand}>
          <Icon
            title={['Non binary', 'Male', 'Female'][supplier.gender]}
            icon={['fas', ['genderless', 'mars', 'venus'][supplier.gender]]}
            className={['text-yellow-600', 'text-green-600', 'text-purple-600'][supplier.gender]}
          />
          <span className="ml-2 group">
            <span>
              { supplier.userid || supplier.uid }
              { beta ?
                <a href={`/suppliers/${supplier.uid}/overview`} target="_blank" className="cursor-pointer ml-2 text-gray-400 group-hover:text-primary">
                  <Icon icon="external-link" />
                </a>
              :
                <a href={`/supplier/${supplier.uid}`} target="_blank" className="cursor-pointer ml-2 text-gray-400 group-hover:text-primary">
                  <Icon icon="external-link" />
                </a>
              }
            </span>
          </span>
        </div>
        <div className="w-1/12 p-2 truncate">
          <FormattedDate value={supplier.initiated_at} />
        </div>
        <div className="w-1/12 p-2 truncate" title={supplier.uid}>
          { supplier.firstname } { supplier.lastname }
        </div>
        <div className="w-2/12 p-2 truncate">{ supplier.email }</div>
        <div className="w-2/12 p-2 flex space-x-3 items-center">
            <DemoPlayer file={supplier.studio} type="Studio" />
            <DemoPlayer file={supplier.audition} type="Audition" />
            <Icon icon="dollar-sign" />
            <input type="checkbox" checked={!!supplier.reviewed_by} onChange={({ target: { checked } }) => onChange({ supplier: { reviewed_by: checked ? id : null, reviewed_at: checked ? new Date() : null }})} />
            <Icon icon="head-side-headphones" />
            <input type="checkbox" checked={!!supplier.quality_checked_by} onChange={({ target: { checked } }) => onChange({ supplier: { quality_checked_by: checked ? id : null,  quality_checked_at: checked ? new Date() : null }})} />
            <Icon
              className={`${!supplier.studio_image ? "cursor-not-allowed text-gray-300" : "cursor-pointer"}`}
              title="Studio setup"
              icon={["fas", "camera"]}
              onClick={() => !supplier.studio_image ? undefined : onStudioSetupPreview(supplier.studio_image)}
            />
        </div>
        <div className="w-4/12 px-4 py-2 flex items-center space-x-1 overflow-hidden">
          {!!(+supplier.isSuspicious) ? (
            <Button 
              variant="inline"
              size="sm"
              onClick={() => onSuspiciousChange()}
            >
              <p className="text-xs">
                Auto-classified as spam. Click here to allow talent to proceed.
              </p>
            </Button>
          ) : (
            <>
              { statuses.find(([id, name, short]) => id == supplier.status) && statuses.map(([id, name, short], i) => (
                <React.Fragment>
                  <button
                    key={id}
                    className={`${supplier.status == id ? 'hover:text-primary' : 'text-gray-400'} cursor-pointer text-sm`}
                    onClick={() => onChange({ status: id })}
                    disabled={supplier.status == id}
                  >
                    <span className="xl:hidden">{ short }</span>
                    <span className="hidden xl:inline-block">{ name }</span>
                  </button>


                  { i < statuses.length - 1 &&
                      <Icon icon="chevron-right" className="text-xs text-gray-400" />
                  }
                </React.Fragment>
              )) }
            </>
          )}
        </div>
        <div className="w-1/12 flex items-center space-x-3 text-md">
          { supplier.standard_rates &&
            <Icon icon={['fas', 'star']} className="ml-1 text-primary fa-fw" title="Accepted standard rates ✔" />
          }

          { supplier.custom_rates &&
            <Icon icon={['fas', 'funnel-dollar']} className="ml-1 text-purple-600 fa-fw" title="Has one or more custom rates ✔" />
          }

          { (supplier.standard_rates || supplier.custom_rates) && roles.includes('sourcing') &&
            <Icon
              icon={['fas', 'trash-undo']}
              className="ml-1 text-negative hover:text-red-400 fa-fw cursor-pointer"
              title="Reset rates"
              onClick={() => {
                if (confirm('DANGER! This will delete ALL rates (both custom and standard) from the voice talent. Do you want to proceed?')) {
                  onReset()
                }
              }}
            />
          }
        </div>
      </div>

      { expanded &&
        <div className="grid grid-cols-3 gap-4 p-4 border-t border-gray-300">
          <p className="border-l-2 border-gray-400 px-2 w-full">
            <div className="text-gray-600 font-medium text-xs mb-2 uppercase">Application</div>
            <span>{ supplier.application || '-' }</span>
          </p>

          <p className="border-l-2 border-gray-400 px-2 w-full">
            <div className="text-gray-600 font-medium text-xs mb-2 uppercase">Setup</div>
            <span>{ supplier.tech_note || '-' }</span>
          </p>

          <p className="border-l-2 border-gray-400 px-2 w-full space-y-2">
            <div>
              <label className="text-gray-600 font-medium text-xs mb-2 uppercase space-x-2">
                <span>Referer</span>
                <Chip state="info">{ supplier.referer }</Chip>
              </label>
              <p>{ supplier.referer_note || '-' }</p>
            </div>

            <div>
              <div className="text-gray-600 font-medium text-xs mb-2 uppercase">
                <span>Voice Age</span>
              </div>
              { supplier.age > 0 ? (
                <p>{ supplier.voiceAge } being { supplier.age} years old</p>
              ) : (
                <p>N/A</p>
              ) }
            </div>
          </p>
        </div>
      }
    </div>
  )
}

export default Supplier
